import Flex from "common/Boxes/Flex";
import Card from "common/Boxes/Card";
import styled, { useTheme } from "styled-components";
import { LanguageNameSpaces, UsageType } from "@shared/types";
import { isWhitelabelSubscriptionType } from "@shared/utils/misc";
import CurrentUsageValue from "features/settings/Quota/CurrentUsageValue";
import StarFilled from "@ant-design/icons/StarFilled";
import UsageChart from "features/settings/Billing/UsageChart";
import Text from "common/Typography/Text";
import QuotaProgressBar from "features/settings/Quota/QuotaProgressBar";
import { getUsageTitleKey } from "@shared/features/settings/utils";
import Button from "common/Buttons/Button";
import {
  PLAN_USAGE_CHART_HEIGHT,
  USAGE_TYPE_TO_ICON,
} from "features/settings/common/constants";
import { useHistory } from "react-router-dom";
import {
  IS_WEB_VIEW,
  PURCHASE_API_SLOTS_SETTINGS_PATH,
} from "common/constants";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import PrimaryApiHelperTooltip from "features/settings/Billing/PrimaryApiHelperTooltip.tsx";
import robotWithScreen from "images/robot_screen.png";
import darkRobotWithScreen from "images/dark_robot_screen.png";
import usePlanUsage from "@shared/features/settings/subscription/hooks/usePlanUsage";
import Link from "common/Typography/Link";
import {
  useHideBillingButtons,
  useUpgradePlanPath,
} from "features/settings/common/hooks.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

const PlanUsage = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const theme = useTheme();
  const history = useHistory();
  const upgradePath = useUpgradePlanPath();

  const {
    user,
    userUsageLimits,
    allQuotaUsage,
    isLoadingQuotas,
    currentUsageTypes,
    activeTradeCurrentUsageValueProps,
  } = usePlanUsage();
  const hideUpgradeButton = useHideBillingButtons();

  const getCurrentUsageCard = () => {
    return (
      <CurrentUsageCard>
        <LoadingSpinnerOverlay isLoading={isLoadingQuotas}>
          <UsageRow style={{ fontWeight: 600 }}>
            {t("plan_usage.features_current_usage")}
          </UsageRow>
          {currentUsageTypes.map((usageType) => {
            const translationKey = getUsageTitleKey(
              usageType,
              user?.subscription_plan_type
            );
            const IconName = USAGE_TYPE_TO_ICON[usageType];
            const icon = IconName ? (
              <IconName style={{ marginRight: 8 }} />
            ) : undefined;
            const showPurchaseApiButton =
              usageType === UsageType.CLIENT &&
              isWhitelabelSubscriptionType(user?.subscription_plan_type) &&
              !user?.isExpired();
            return (
              <UsageRow key={usageType}>
                <Flex>
                  {icon}
                  <Text as="div">
                    {translationKey && tCommon(translationKey)}
                    {showPurchaseApiButton && (
                      <>
                        <PrimaryApiHelperTooltip />
                        {!IS_WEB_VIEW && (
                          <Link
                            style={{ marginLeft: 8 }}
                            onClick={() =>
                              history.push(PURCHASE_API_SLOTS_SETTINGS_PATH)
                            }
                          >
                            {t("plan_usage.purchase")}
                          </Link>
                        )}
                      </>
                    )}
                  </Text>
                </Flex>
                <CurrentUsageValue
                  usageType={usageType}
                  currentUsage={allQuotaUsage?.[usageType]}
                  usageLimit={userUsageLimits?.[usageType]}
                />
              </UsageRow>
            );
          })}
        </LoadingSpinnerOverlay>
      </CurrentUsageCard>
    );
  };

  const getTradesUpgradeContent = () => {
    let content = (
      <>
        <Text
          type={theme.isDarkMode ? undefined : "secondary"}
          style={{
            marginBottom: 12,
            maxWidth: 170,
            textAlign: "center",
          }}
        >
          {t("plan_usage.dont_miss_trades")}
        </Text>
        {!hideUpgradeButton && (
          <Button onClick={() => history.push(upgradePath)} $type="outlined">
            <StarFilled />
            {t("plan_usage.upgrade")}
          </Button>
        )}
      </>
    );
    if (user?.isPro() || user?.isMaxSubscription()) {
      content = (
        <StyledImage
          src={theme.isDarkMode ? darkRobotWithScreen : robotWithScreen}
        />
      );
    }
    return (
      <Flex
        style={{ flex: 1, width: "100%", overflow: "hidden" }}
        $justifyContent="center"
        $flexDirection="column"
      >
        {content}
      </Flex>
    );
  };

  const getTradesChartCard = () => {
    const tradeCurrentUsage = allQuotaUsage?.[UsageType.ACTIVE_TRADE];
    const tradeUsageLimit = userUsageLimits?.[UsageType.ACTIVE_TRADE];
    let percent = 100;
    if (
      typeof tradeCurrentUsage === "number" &&
      typeof tradeUsageLimit === "number"
    ) {
      percent = Math.max(100, (tradeCurrentUsage / tradeUsageLimit) * 100);
    }
    const Icon = USAGE_TYPE_TO_ICON[UsageType.ACTIVE_TRADE];
    return (
      <TradesChartCard>
        <LoadingSpinnerOverlay isLoading={isLoadingQuotas}>
          <UsageRow
            style={{
              fontWeight: 600,
              marginBottom: 12,
              justifyContent: "flex-start",
            }}
          >
            <Flex $justifyContent="flex-start">
              <Icon style={{ marginRight: 8, fontSize: 16 }} />{" "}
              {t("plan_usage.signal_trades")}
            </Flex>
          </UsageRow>
          <OpenTradesWrapper>
            <TradesInfoWrapper>
              <Flex>
                <Text>{t("plan_usage.currently_open")}</Text>
                <CurrentUsageValue {...activeTradeCurrentUsageValueProps} />
              </Flex>
              <QuotaProgressBar percent={percent} />
              {getTradesUpgradeContent()}
            </TradesInfoWrapper>
            <ChartWrapper>
              <UsageChart />
            </ChartWrapper>
          </OpenTradesWrapper>
        </LoadingSpinnerOverlay>
      </TradesChartCard>
    );
  };

  return (
    <Wrapper>
      {getTradesChartCard()}
      {getCurrentUsageCard()}
    </Wrapper>
  );
};

export default PlanUsage;

const VERTICAL_PAGE_CONTENT_BREAKPOINT = 1000;
const VERTICAL_TRADES_CONTENT_BREAKPOINT = 630;

const Wrapper = styled.div`
  margin-top: 16px;
  display: flex;
  column-gap: 16px;
  row-gap: 16px;
  align-items: flex-start;
  @media (max-width: ${VERTICAL_PAGE_CONTENT_BREAKPOINT}px) {
    flex-direction: column;
  }
`;
const OpenTradesWrapper = styled.div`
  display: flex;
  column-gap: 16px;
  align-items: flex-start;
  row-gap: 16px;
  @media (max-width: ${VERTICAL_TRADES_CONTENT_BREAKPOINT}px) {
    flex-direction: column;
  }
`;
const StyledCard = styled(Card)`
  flex: 1;
`;
const CurrentUsageCard = styled(StyledCard)`
  position: relative;
  flex: 1 0 0;
  max-width: 350px;
  padding-top: 4px;
  padding-bottom: 4px;
  @media (max-width: ${VERTICAL_PAGE_CONTENT_BREAKPOINT}px) {
    width: 100%;
    max-width: none;
  }
`;
const TradesChartCard = styled(StyledCard)`
  position: relative;
  padding-top: 4px;
  flex: 2;
  @media (min-width: ${VERTICAL_TRADES_CONTENT_BREAKPOINT}px) {
    min-width: 450px;
  }
`;
const UsageRow = styled.div`
  color: ${(p) => p.theme.colors.primaryText};
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 50px;
  &:not(:last-of-type) {
    border-bottom: ${(p) => p.theme.borders.light};
  }
  &:last-of-type {
    padding-bottom: 0;
  }
  &:first-of-type {
    padding-top: 0;
  }
`;
const ChartWrapper = styled.div`
  flex: 1 1 380px;
  height: ${PLAN_USAGE_CHART_HEIGHT}px;
  overflow: hidden;
  @media (max-width: ${VERTICAL_TRADES_CONTENT_BREAKPOINT}px) {
    width: 100%;
    flex: 1 1 ${PLAN_USAGE_CHART_HEIGHT}px;
  }
`;
const TradesInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px;
  background-color: ${(p) =>
    p.theme.isDarkMode ? p.theme.colors.mediumBgColor : "#ECEDF04D"};
  border-radius: 8px;
  height: ${PLAN_USAGE_CHART_HEIGHT}px;
  flex: 1 0 190px;
  @media (max-width: ${VERTICAL_TRADES_CONTENT_BREAKPOINT}px) {
    width: 100%;
  }
`;
const StyledImage = styled.img`
  height: 90%;
`;
